'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { ApolloPanel } from '@/components/ui/apollo-panel'
import { Button } from '@/components/ui/button'
import { TaskHistoryDialog } from '@/components/tasks/task-history-dialog'
import { useAuthStore } from '@/stores/auth-store'
import {
  AGENT_QUEUE_BUCKETS,
  canUseAgentWorkQueue,
  getAgentWorkQueue,
  type AgentQueueBucket,
  type AgentWorkQueue
} from '@/lib/agent-work-queue-api'
import type { Task } from '@/lib/tasks-api'

export function AgentWorkQueuePanel() {
  const user = useAuthStore((state) => state.user)
  if (!canUseAgentWorkQueue(user) || !user) return null
  return <QueueContent key={user.id} />
}

function QueueContent() {
  const t = useTranslations('agentWorkQueue')
  const locale = useLocale()
  const [query, setQuery] = useState<{ bucket: AgentQueueBucket; page: number }>({
    bucket: 'overdue',
    page: 1
  })
  const [data, setData] = useState<AgentWorkQueue | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [history, setHistory] = useState<Task | null>(null)
  const request = useRef<AbortController | null>(null)
  const load = useCallback(async () => {
    request.current?.abort()
    const current = new AbortController()
    request.current = current
    setStatus('loading')
    setData(null)
    try {
      const result = await getAgentWorkQueue(query.bucket, query.page, current.signal)
      if (!current.signal.aborted) {
        setData(result)
        setStatus('ready')
      }
    } catch {
      if (!current.signal.aborted) setStatus('error')
    }
  }, [query.bucket, query.page])
  useEffect(() => {
    const timer = setTimeout(() => {
      void load()
    }, 0)
    return () => {
      clearTimeout(timer)
      request.current?.abort()
    }
  }, [load])
  const navigate = (bucket: AgentQueueBucket, page = 1) => {
    request.current?.abort()
    setData(null)
    setStatus('loading')
    setQuery({ bucket, page })
  }
  const date = (value: string) =>
    new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: data?.timezone
    }).format(new Date(value))
  const empty = data && data.tasks.length === 0 && data.leads.length === 0
  return (
    <div data-testid="agent-work-queue">
      <ApolloPanel
        title={<h2 className="font-semibold">{t('title')}</h2>}
        headerClassName="flex-wrap items-start"
        actions={
          <Link href="/tasks" className="text-sm font-medium text-primary hover:underline">
            {t('allTasks')}
          </Link>
        }
      >
        <p className="mb-4 text-sm text-muted-foreground">{t('description')}</p>
        <div role="group" aria-label={t('bucketsLabel')} className="mb-4 flex flex-wrap gap-2">
          {AGENT_QUEUE_BUCKETS.map((bucket) => (
            <Button
              key={bucket}
              variant={query.bucket === bucket ? 'default' : 'outline'}
              size="sm"
              aria-pressed={query.bucket === bucket}
              onClick={() => {
                if (bucket !== query.bucket) navigate(bucket)
              }}
              className="h-auto min-h-9 whitespace-normal text-left"
            >
              {t('buckets.' + bucket)}{' '}
              <span className="ml-1 tabular-nums">
                {data ? new Intl.NumberFormat(locale).format(data.totals[bucket]) : '—'}
              </span>
              <span className="sr-only">
                {t(bucket === 'needs_next_step' ? 'leadsUnit' : 'tasksUnit')}
              </span>
            </Button>
          ))}
        </div>
        <p className="mb-3 text-xs text-muted-foreground">{t('separateCounts')}</p>
        {status === 'loading' ? (
          <p role="status" className="py-6 text-sm text-muted-foreground">
            {t('loading')}
          </p>
        ) : status === 'error' ? (
          <div role="alert" className="space-y-3 py-4">
            <p className="text-sm text-destructive">{t('loadFailed')}</p>
            <Button
              variant="outline"
              onClick={() => {
                void load()
              }}
            >
              {t('retry')}
            </Button>
          </div>
        ) : (
          data && (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                <p className="text-sm font-medium">
                  {t(data.count_unit === 'leads' ? 'leadCount' : 'taskCount', {
                    count: data.pagination.total
                  })}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void load()
                  }}
                >
                  {t('refresh')}
                </Button>
                <p className="w-full text-xs text-muted-foreground">
                  {t('snapshot', { date: date(data.evaluated_at), timezone: data.timezone })}
                </p>
              </div>
              {empty ? (
                <div className="space-y-3 py-5 text-sm text-muted-foreground">
                  <p>{t(query.page > 1 ? 'emptyPage' : 'emptyBucket')}</p>
                  {query.page > 1 && (
                    <Button variant="outline" onClick={() => navigate(query.bucket)}>
                      {t('firstPage')}
                    </Button>
                  )}
                </div>
              ) : (
                <ul className="divide-y" aria-label={t('buckets.' + query.bucket)}>
                  {data.tasks.map((task) => (
                    <li
                      key={task.id}
                      className="grid gap-3 py-4 first:pt-1 md:grid-cols-[minmax(0,1fr)_minmax(160px,auto)_auto] md:items-center"
                    >
                      <div className="min-w-0 space-y-1">
                        {task.lead && (
                          <Link
                            href={'/leads/' + task.lead.id + '#next-action'}
                            className="break-words text-sm font-semibold text-primary hover:underline"
                          >
                            {task.lead.full_name}
                          </Link>
                        )}
                        <button
                          type="button"
                          className="block text-left text-sm underline-offset-4 hover:underline"
                          aria-label={t('historyFor', { title: task.title })}
                          onClick={() => setHistory(task)}
                        >
                          {task.title}
                        </button>
                        {(task.purpose && t.has('purposes.' + task.purpose)) && (
                          <p className="text-xs text-muted-foreground">
                            {t('purposes.' + task.purpose)}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1 text-sm">
                        <p className={task.is_overdue ? 'font-medium text-destructive' : ''}>
                          {task.due_at ? date(task.due_at) : t('unscheduled')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('owner', { name: task.assignee?.full_name ?? t('unknownOwner') })}
                        </p>
                      </div>
                      {task.lead && (
                        <Button asChild variant="outline" size="sm" className="w-fit">
                          <Link href={'/leads/' + task.lead.id + '#next-action'}>
                            {t('openLead')}
                          </Link>
                        </Button>
                      )}
                    </li>
                  ))}
                  {data.leads.map((lead) => (
                    <li
                      key={lead.id}
                      className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-1"
                    >
                      <div className="min-w-0 space-y-1">
                        <p className="break-words text-sm font-semibold">{lead.full_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {t('reasons.' + lead.reason)}
                        </p>
                      </div>
                      <Button asChild variant="outline" size="sm">
                        <Link href={'/leads/' + lead.id + '#next-action'}>{t('reviewLead')}</Link>
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                <p className="text-xs text-muted-foreground">
                  {t('page', {
                    current: data.pagination.current_page,
                    total: data.pagination.last_page
                  })}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={query.page <= 1}
                    onClick={() => navigate(query.bucket, query.page - 1)}
                  >
                    {t('previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={query.page >= data.pagination.last_page}
                    onClick={() => navigate(query.bucket, query.page + 1)}
                  >
                    {t('next')}
                  </Button>
                </div>
              </div>
            </>
          )
        )}
      </ApolloPanel>
      {history && (
        <TaskHistoryDialog key={history.id} task={history} onClose={() => setHistory(null)} />
      )}
    </div>
  )
}
